// File: frontend/src/data/publications.ts
// Peer-reviewed publications related to TI Mindmap HUB research.
// Only external links are used: no PDFs are hosted on the platform.

export interface Publication {
    id: string;
    title: string;
    authors: { name: string; affiliation: string }[];
    venue: string;
    venueShort: string;
    publisher: string;
    year: number;
    doi: string;
    url: string;
    keywords: string[];
    summary: string;
    highlights: string[];
}

export const publications: Publication[] = [
    {
        id: 'castiglione2026genaistix',
        title: 'From Threat Reports to Structured Cyber Threat Intelligence Using LLMs',
        authors: [
            { name: 'Arcangelo Castiglione', affiliation: 'University of Salerno' },
            { name: 'Giulio Triggiani', affiliation: 'University of Salerno' },
            { name: 'Antonio Formato', affiliation: 'Microsoft Italy' },
            { name: 'Kim-Kwang Raymond Choo', affiliation: 'University of Texas at San Antonio' },
        ],
        venue: 'IEEE Consumer Electronics Magazine',
        venueShort: 'IEEE CEM',
        publisher: 'IEEE Consumer Technology Society',
        year: 2026,
        doi: '10.1109/MCE.2026.3737890',
        url: 'https://ieeexplore.ieee.org/document/11715786',
        keywords: [
            'Large language models',
            'Cyber threat intelligence',
            'Generative Pre-trained transformer',
            'Malware',
            'Grounding',
            'Internet of Things',
            'Computer security',
        ],
        summary:
            'Presents GenAI-STIX, a hybrid architecture that combines deterministic IoC extraction with LLM-powered semantic inference to automate end-to-end STIX 2.1 bundle generation from unstructured threat reports.',
        highlights: [
            '>93% precision on TTP extraction (GPT-5, Gemini 2.5 Pro)',
            '83%+ semantic similarity on MITRE ATT&CK mappings',
            'Evaluated on 16 official NCSC malware analysis reports',
            '48%+ indicator F1 with open-weight Llama 4 models',
        ],
    },
];

export const doiUrl = (p: Publication): string => `https://doi.org/${p.doi}`;

export const formatAuthorsShort = (p: Publication): string => {
    const names = p.authors.map((a) => {
        const parts = a.name.split(' ');
        const last = parts.pop();
        return `${parts.map((n) => n.split('-').map((s) => `${s[0]}.`).join('-')).join(' ')} ${last}`;
    });
    return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
};

export const formatIeeeCitation = (p: Publication): string =>
    `${formatAuthorsShort(p)}, "${p.title}," in ${p.venue}, doi: ${p.doi}.`;

export const formatBibtex = (p: Publication): string =>
    [
        `@article{${p.id},`,
        `  title     = {${p.title}},`,
        `  author    = {${p.authors.map((a) => a.name).join(' and ')}},`,
        `  journal   = {${p.venue}},`,
        `  publisher = {IEEE},`,
        `  year      = {${p.year}},`,
        `  doi       = {${p.doi}},`,
        `  url       = {${p.url}}`,
        `}`,
    ].join('\n');
